import type { MaybeRef } from 'vue'
import type { ContentFormWriter } from '~/models/Content/ContentFormWriter'
import type { AssignmentAnswer, AssignmentResponse } from '~/models/Assignment'
import { useI18n } from 'vue-i18n'
import { computed, inject, unref } from 'vue'
import { storeToRefs } from 'pinia'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import useAuthStore from '~/stores/auth'
import useMetadataApi from '~/api/metadataApi'
import useContentApi from '~/api/contentApi'
import useAssignmentApi from '~/api/assignmentApi'

export default function useFormWriter(
  locationId: number,
  userId?: MaybeRef<string | undefined>,
  isAssignment: boolean = false,
  isPersistent: boolean = false
) {
  const { t } = useI18n()
  const toast = inject('ksToast') as Toast
  const queryClient = useQueryClient()
  const { isTeacher } = storeToRefs(useAuthStore())

  const { findContents } = useContentApi()
  const { get: getMetadata, set: setMetadata } = useMetadataApi()
  const { getStudentAssignmentResponse, saveStudentAssignment, createStudentAssignment } = useAssignmentApi()

  const assignmentId = `form_writer-${locationId}`
  const metadataId = `formWriter-${locationId}`

  const currentUserId = computed(() => unref(userId))

  const assignmentQueryKey = computed(() => ['form_writer_assignment', assignmentId, currentUserId.value])
  const metadataQueryKey = computed(() => ['form_writer_metadata', metadataId, currentUserId.value])

  const assignmentEnabled = computed(() => !!locationId && !!currentUserId.value && isAssignment)
  const metadataEnabled = computed(() => !!locationId && isPersistent && !(isTeacher.value && isAssignment))

  const {
    data,
    isLoading: isLoadingContent,
    isError
  } = useQuery({
    queryKey: ['form_writer', locationId],
    queryFn: async () => (await findContents<ContentFormWriter>({
      locationIdCriterion: [locationId],
      mainLocationCriterion: true,
    }))[0],
    staleTime: Infinity,
    enabled: !!locationId
  })

  const {
    data: assignment,
    isLoading: isLoadingAssignment
  } = useQuery({
    queryKey: assignmentQueryKey,
    queryFn: () => getStudentAssignmentResponse(assignmentId, currentUserId.value as string),
    staleTime: Infinity,
    enabled: assignmentEnabled,
    retry: false,
  })

  const {
    data: metadata,
    isLoading: isLoadingMetadata
  } = useQuery({
    queryKey: metadataQueryKey,
    queryFn: () => getMetadata<{ key: string, userId: string, value: any }>(metadataId),
    staleTime: Infinity,
    enabled: metadataEnabled,
    retry: false,
  })

  // This is structured so we only fetch a fresh assignment the first time we enter something
  const updateAssignment = useMutation({
    mutationFn: async (answer: AssignmentAnswer) =>
      saveStudentAssignment(assignmentId, currentUserId.value as string, answer),
    onSuccess: (data) => {
      // Check if we have existing data in the cache
      const existingData = queryClient.getQueryData(assignmentQueryKey.value) as AssignmentResponse | undefined

      // Invalidate if no data exists or if answer array is empty
      if (!existingData || !existingData.answer || existingData.answer.length === 0) {
        // If no existing data or empty answer array, invalidate the query to fetch fresh data
        void queryClient.invalidateQueries({ queryKey: assignmentQueryKey.value })
      } else {
        // If we have existing data with answers, update it in the cache without refetching
        queryClient.setQueryData(assignmentQueryKey.value, (oldData: AssignmentResponse) => ({
          ...oldData,
          answer: oldData.answer.map(item =>
            item.isDraft ? data : item
          )
        }))
      }
    }
  })

  const updateMetadata = useMutation({
    mutationFn: async (value: { image: string, fields: string[] }) =>
      setMetadata(metadataId, value, currentUserId.value as string),
    onSuccess: data => queryClient.setQueryData(metadataQueryKey.value, () => data),
  })

  const saveAssignment = useMutation({
    mutationFn: async (answer: AssignmentAnswer) =>
      createStudentAssignment(assignmentId, currentUserId.value as string, answer),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assignmentQueryKey.value }),
    onSettled: (_, error) => error
      ? toast.error(t('formWriter.toasts.submitError'))
      : toast.success(t('formWriter.toasts.submitSuccess')),
  })

  const updateForm = (formData: { image: string, fields: string[] }) => {
    if (isAssignment && assignment.value) {
      const draft = assignment.value?.answer.find(({ isDraft }) => isDraft)
      const draftAnswer = draft ? JSON.parse(draft?.data) : {}

      void updateAssignment.mutateAsync({
        ...draftAnswer,
        isDraft: true,
        data: JSON.stringify(formData)
      })
    }

    if (isPersistent) {
      void updateMetadata.mutateAsync(formData)
    }
  }

  return {
    data,
    assignment,
    metadata,

    isLoadingFormData: computed(() => isLoadingContent.value || isLoadingAssignment.value || isLoadingMetadata.value),
    isError,

    updateForm,
    saveAssignment,
  }
}
